import { Subject } from "rxjs";
import { isBrowser } from "../messages/webAppMessenger";
import isEqual from "lodash/isEqual";

export default class QRCodeScanner {
  constructor() {
    this.scanner = window.QRScanner;
    this.haveScanner = !!this.scanner;
    this.isAvailable = false;
    this.statusUpdates = new Subject();
    this.isScanningCheckTimeout = null;
    this.status = {
      authorized: false,
      denied: false,
      restricted: false,
      prepared: false,
      scanning: false,
      previewing: false,
      showing: false,
      lightEnabled: false,
      canOpenSettings: false,
      canEnableLight: false,
      canChangeCamera: false,
      currentCamera: false
    };
    if (!this.haveScanner) {
      console.error("NO QRScanner!!!");
    }
  }

  onStatusUpdate(handler) {
    this.statusUpdates.subscribe(handler);
  }

  statusUpdated(newStatus) {
    if (newStatus && !isEqual(this.status, newStatus)) {
      this.status = newStatus;
      this.sendStatus();
    }
  }

  sendStatus() {
    this.statusUpdates.next(this.status);
  }

  prepare() {
    // console.log("QRCodeScanner prepare");
    return new Promise((resolve, reject) => {
      this.scanner.prepare((error, status) => {
        if (error) {
          reject(error);
        } else {
          this.statusUpdated(status);
          resolve(status);
        }
      });
    });
  }

  openSettings() {
    this.scanner.openSettings();
  }

  getStatus() {
    return new Promise(resolve => {
      this.scanner.getStatus(status => {
        this.statusUpdated(status);
        resolve(status);
      });
    });
  }

  show() {
    // console.log("QRCodeScanner show");
    return new Promise(resolve => {
      this.scanner.show(status => {
        this.statusUpdated(status);
        resolve(status);
      });
    });
  }

  hide() {
    // console.log("QRCodeScanner hide");
    return new Promise(resolve => {
      this.scanner.hide(status => {
        this.statusUpdated(status);
        resolve(status);
      });
    });
  }

  scan() {
    // console.log("QRCodeScanner scan");
    return new Promise((resolve, reject) => {
      this.scanner.scan(async (error, contents) => {
        await this.pausePreview();
        if (error) {
          if (error.code !== 6) {
            // CANCELED
            reject(error);
          }
        } else {
          resolve(isBrowser() ? contents.result : contents);
        }
      });
    });
  }

  cancelScan() {
    // console.log("QRCodeScanner cancelScan");
    return new Promise(resolve => {
      this.scanner.cancelScan(status => {
        this.statusUpdated(status);
        resolve(status);
      });
    });
  }

  destroy() {
    // console.log("QRCodeScanner destroy");
    return new Promise(resolve => {
      this.scanner.destroy(status => {
        this.statusUpdated(status);
        resolve(status);
      });
    });
  }

  pausePreview() {
    // console.log("QRCodeScanner pausePreview");
    return new Promise(resolve => {
      this.scanner.pausePreview(status => {
        this.statusUpdated(status);
        resolve(status);
      });
    });
  }

  resumePreview() {
    // console.log("QRCodeScanner resumePreview");
    return new Promise(resolve => {
      this.scanner.resumePreview(status => {
        this.statusUpdated(status);
        resolve(status);
      });
    });
  }

  enableLight() {
    return new Promise((resolve, reject) => {
      this.scanner.enableLight((error, status) => {
        if (error) {
          reject(error);
        } else {
          this.statusUpdated(status);
          resolve(status);
        }
      });
    });
  }

  disableLight() {
    return new Promise((resolve, reject) => {
      this.scanner.disableLight((error, status) => {
        if (error) {
          reject(error);
        } else {
          this.statusUpdated(status);
          resolve(status);
        }
      });
    });
  }

  useFrontCamera() {
    return new Promise((resolve, reject) => {
      this.scanner.useFrontCamera((error, status) => {
        if (error) {
          reject(error);
        } else {
          this.statusUpdated(status);
          resolve(status);
        }
      });
    });
  }

  useBackCamera() {
    return new Promise((resolve, reject) => {
      this.scanner.useBackCamera((error, status) => {
        if (error) {
          reject(error);
        } else {
          this.statusUpdated(status);
          resolve(status);
        }
      });
    });
  }

  useCamera(camera) {
    return new Promise((resolve, reject) => {
      this.scanner.useCamera(camera, (error, status) => {
        if (error) {
          reject(error);
        } else {
          this.statusUpdated(status);
          resolve(status);
        }
      });
    });
  }

  async checkIfScanningHasStarted() {
    // console.log("checkIfScanningHasStarted", this.status.scanning);
    if (!this.status.scanning) {
      const newStatus = await this.getStatus();
      if (!newStatus.scanning) {
        this.isScanningCheckTimeout = setTimeout(this.checkIfScanningHasStarted.bind(this), 500);
      }
    }
  }

  async startScanning(onCodeRecognized) {
    this.sendStatus();
    console.log("Starting to Scan", this.status);
    if (!this.status.prepared) {
      if (window.device.platform === "browser") {
        // In the browser we need to get user media first so it will ask for permissions.
        try {
          const mediaStream = await window.navigator.mediaDevices.getUserMedia({
            audio: false,
            video: true
          });
          mediaStream.getTracks().forEach(track => track.stop());
        } catch (error) {
          console.error(error);
        }
      }
      await this.prepare();
    }
    if (this.status.authorized) {
      if (!this.status.showing) {
        await this.show();
      }
      if (!this.status.previewing) {
        await this.resumePreview();
      }
      document.documentElement.classList.add("scanning");
      this.scan().then(onCodeRecognized);
      setTimeout(this.checkIfScanningHasStarted.bind(this), 100);
    } else if (this.status.denied) {
      if (this.status.canOpenSettings) {
        this.openSettings();
      }
      throw new Error("Camera Access Denied.  You must change this in the settings.");
    } else {
      throw new Error("Unable to start the scanner.");
    }
  }

  async stopScanning() {
    console.log("QRCodeScanner stopScanning");
    if (this.isScanningCheckTimeout) {
      clearTimeout(this.isScanningCheckTimeout);
    }
    if (this.status.lightEnabled) {
      await this.disableLight();
    }
    if (this.status.scanning) {
      await this.cancelScan();
    }
    if (this.status.showing) {
      await this.hide();
    }
    document.documentElement.classList.remove("scanning");
  }
}
